import useBemClass from '@/utils/hooks/useBEMClass'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRef } from 'react'
import G2, { Chart } from '@antv/g2'
import './good-sale-chart.scss'
import { useRequest, useUpdateEffect } from 'ahooks'
import { $post } from '@/api'
import { GoodSaleByCategory, GoodSaleByGroup, GoodSaleByName } from '../type'
import Big from 'big.js'
import { get, isExist } from '@/utils'
import GoodSaleIncome from '@/assets/images/good-sale-income.svg'
import { Center, Row } from '@/components/layout/layout'
import { Empty, Spin } from 'antd'
import { goodSaleFilterStore } from '../good-sale-filter/store'
import { observer } from 'mobx-react'

interface GoodSaleChartProps {
    saleAmount: number
    type: number
    date: (string | undefined)[]
}

interface GoodSaleChart {
    item: string
    count: number
    percent: number
}

const GoodSaleChart: React.FC<GoodSaleChartProps> = observer(props => {
    const cls = useBemClass('good-sale-chart')
    const chartsRef = useRef<G2.Chart | null>(null)

    const [chartData, setChartData] = useState<GoodSaleChart[]>([])
    const [noData, setNoData] = useState<boolean>(false)

    const chartParams = useMemo(() => {
        return {
            page: 1,
            limit: 5,
            from_date: props.date[0],
            to_date: props.date[1],
            ordering_desc: true,
        }
    }, [props.date])

    const computeChartData = useCallback(
        (
            res: any,
            key: string,
            sortKey: string = 'net_sales_income',
            totalSortKey: string = 'sum_net_sales_income',
        ) => {
            if (!res.goods_data_list.length) {
                return setNoData(true)
            }
            noData && setNoData(false)
            const topFiveTotalSales = +res.goods_data_list.slice(0, 5).reduce((pre: any, next: any) => {
                return pre.plus(next[sortKey])
            }, Big(0))
            const elseSale = +Big(res[totalSortKey]).minus(topFiveTotalSales).round(4)
            const chartData = [
                ...res.goods_data_list
                    .slice(0, 5)
                    .map((l: any) => ({
                        item: get(l, key, ''),
                        count: l[sortKey],
                        percent: +Big(l[sortKey])
                            .div(res[totalSortKey] || 1)
                            .round(4),
                    }))
                    .filter((r: any) => r.count),
                elseSale
                    ? {
                          item: '其他',
                          count: elseSale,
                          percent: +Big(elseSale)
                              .div(res[totalSortKey] || 1)
                              .round(4),
                      }
                    : {},
            ].filter((v: any) => isExist(v))
            setChartData(chartData as GoodSaleChart[])
        },
        [noData],
    )

    const goodNameCahrtReq = useRequest<GoodSaleByName.Data>(
        () =>
            $post({
                url: '/finance/getgoodssalesstatistics',
                params: {
                    ordering_type: 2,
                    ...chartParams,
                    ...goodSaleFilterStore.goodNameFilterQuery,
                },
            }),
        {
            manual: true,
            onSuccess: res => {
                computeChartData(res, 'goods.name')
            },
        },
    )

    const goodCategoryCahrtReq = useRequest<GoodSaleByCategory.Data>(
        () =>
            $post({
                url: '/finance/getgoodssalesstatisticsbycategory',
                params: {
                    ordering_type: 3,
                    ...chartParams,
                    ...goodSaleFilterStore.goodCategoryFilterQuery,
                },
            }),
        {
            manual: true,
            onSuccess: res => {
                computeChartData(res, 'category_name')
            },
        },
    )

    const goodGroupCahrtReq = useRequest<GoodSaleByGroup.Data>(
        () =>
            $post({
                url: '/finance/getgoodssalesstatisticsbygroup',
                params: {
                    ordering_type: 3,
                    ...chartParams,
                    ...goodSaleFilterStore.goodGroupFilterQuery,
                },
            }),
        {
            manual: true,
            onSuccess: res => {
                computeChartData(res, 'group_name')
            },
        },
    )

    const chartReqMap = [goodNameCahrtReq, goodCategoryCahrtReq, goodGroupCahrtReq]

    useEffect(() => {
        chartReqMap[props.type].run()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.type, chartParams, goodSaleFilterStore.goodNameFilterQuery])

    useUpdateEffect(() => {
        if (chartsRef.current) chartsRef.current.destroy()
        chartsRef.current = new Chart({
            container: 'good-sale-container',
            autoFit: true,
            height: 250,
        })

        const colorSet = ['#04635A', '#068E6B', '#08B57D', '#22DB63', '#65EC66', '#f5f5f5']

        chartsRef.current.coordinate('theta', {
            radius: 0.75,
            innerRadius: 0.6,
        })

        chartsRef.current.data(chartData)

        chartsRef.current.scale('percent', {
            formatter: val => {
                val = val * 100 + '%'

                return val
            },
        })

        chartsRef.current.tooltip(false)

        // 声明需要进行自定义图例字段： 'item'
        chartsRef.current.legend('item', {
            position: 'left', // 配置图例显示位置
            custom: true, // 关键字段，告诉 G2，要使用自定义的图例
            items: chartData.map((obj, index) => {
                return {
                    name: obj.item, // 对应 itemName
                    value: '¥' + obj.count, // 对应 itemValue
                    marker: {
                        symbol: 'circle', // marker 的形状
                        style: {
                            r: 5,
                            fill: colorSet[index],
                        },
                    }, // marker 配置
                }
            }),
            itemName: {
                style: {
                    fill: '#666',
                    fontSize: 14,
                },
            },
            // itemValue: {
            //     style: {
            //         fill: '#000',
            //         fontSize: 14,
            //         fontFamily: 'Bebas',
            //     }, // 配置 itemValue 样式
            //     // formatter: val => `${+val * 100}%`, // 格式化 itemValue 内容
            // },
        })

        chartsRef.current
            .interval()
            .adjust('stack')
            .position('percent')
            .color('item', colorSet)
            .style({
                fillOpacity: 1,
            })
            .state({
                active: {
                    style: () => {
                        return {
                            lineWidth: 10,
                        }
                    },
                },
            })

        // 移除图例点击过滤交互
        chartsRef.current.removeInteraction('legend-filter')
        chartsRef.current.interaction('element-active')

        chartsRef.current.render()

        // 监听 element 上状态的变化来动态更新 Annotation 信息
        chartsRef.current.on('element:statechange', (ev: any) => {
            const { state, stateStatus, element } = ev.gEvent.originalEvent

            // 本示例只需要监听 active 的状态变化
            if (state === 'active') {
                const data = element.getData()
                if (stateStatus) {
                    // 更新 Annotation
                    updateAnnotation(data)
                } else {
                    // 隐藏 Annotation
                    clearAnnotation()
                }
            }
        })

        let lastItem: any
        function updateAnnotation(data: any) {
            if (data.item !== lastItem) {
                chartsRef.current!.annotation().clear(true)
                chartsRef
                    .current!.annotation()
                    .text({
                        position: ['50%', '50%'],
                        content: `${+Big(data.percent).times(100)}%`,
                        style: {
                            fontSize: 22,
                            fill: '#000',
                            fontWeight: 600,
                            textAlign: 'center',
                        },
                        offsetY: -10,
                    })
                    .text({
                        position: ['50%', '50%'],
                        content: data.item.length > 6 ? `${data.item.substring(0, 6)}...` : data.item,
                        style: {
                            fontSize: 12,
                            fill: '#999',
                            textAlign: 'center',
                        },
                        offsetY: 14,
                    })
                chartsRef.current!.render(true)
                lastItem = data.item
            }
        }

        // 清空 annotation
        function clearAnnotation() {
            chartsRef.current!.annotation().clear(true)
            chartsRef.current!.render(true)
            lastItem = null
        }
    }, [chartData])

    return (
        <div className={cls()}>
            <div className={cls('top')}>
                <Row data-align-items-center>
                    <img alt="" src={GoodSaleIncome} width="24" height="24" data-margin-right-8 />
                    <div data-color-333 data-font-size-16>
                        销售额
                    </div>
                </Row>
                <div className={cls('top', 'price')}>¥{props.saleAmount}</div>
            </div>
            <div data-position-relative className={cls('chart-container')}>
                <Spin spinning={chartReqMap[props.type].loading}>
                    {noData ? (
                        <Center data-expaned>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </Center>
                    ) : (
                        <>
                            <div className={cls('count-container')}>
                                {chartData.map((d, i) => (
                                    <div key={i}>￥{d.count}</div>
                                ))}
                            </div>
                            <div
                                id="good-sale-container"
                                data-padding-h-20
                                style={{ width: '120%', height: '250px' }}
                            />
                        </>
                    )}
                </Spin>
            </div>
        </div>
    )
})

export default GoodSaleChart
